import React, { useEffect, useState, useCallback } from "react";
import { ClaySelect, ClayInput } from "@clayui/form";
import ClayButton from "@clayui/button";
import ClayLayout from "@clayui/layout";
import { Table } from "@clayui/core";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchEnergyData,
  fetchEnergyDataById,
  saveEnergyData,
  deleteEnergyData,
  downloadEnergyTemplate,
  uploadEnergyData,
  synchronizeAllData,
} from "../../api/energyDataService";
import {
  fetchEnergyDataTypes,
  fetchEnergyDataCategories,
  fetchEnergyDataTemplateByCategoryAndType,
  updateEnergyDataTemplate,
} from "../../api/energyDataTemplateService";
import { useModal } from "@clayui/modal";
import EnergyDataModal from "./EnergyDataModal";
import TemplateDataModal from "./TemplateDataModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Pagination from "../../components/Pagination";
import * as XLSX from "xlsx";

function EnergyDataFormList() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [typeList, setTypeList] = useState([]);

  const [selectedYearFrom, setSelectedYearFrom] = useState(null);
  const [selectedYearTo, setSelectedYearTo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const { observer, onClose } = useModal({
    onClose: () => setShowModal(false),
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateJson, setTemplateJson] = useState({});

  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Progress States
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      const searchParams = {
        yearFrom: selectedYearFrom ? selectedYearFrom.getFullYear() : "",
        yearTo: selectedYearTo ? selectedYearTo.getFullYear() : "",
        category: selectedCategory,
        type: selectedType,
        page: currentPage - 1,
        size: pageSize,
      };
      const result = await fetchEnergyData(searchParams);
      setData(result.content || []);
      setFilteredData(result.content || []);
      setTotalPages(result.totalPages || 0);
      setTotalElements(result.totalElements || 0);

      if (selectedCategory && selectedType) {
        const template = await fetchEnergyDataTemplateByCategoryAndType(
          selectedCategory,
          selectedType
        );
        setSelectedTemplate(template);
      }
    };

    fetchData();
  }, [
    selectedYearFrom,
    selectedYearTo,
    selectedCategory,
    selectedType,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const types = await fetchEnergyDataTypes();
        setTypeList(types);
      } catch (error) {
        console.error("Error fetching types:", error);
      }
    };

    loadTypes();
  }, []);

  const handleTypeChange = async (event) => {
    const selectedTypeCode = event.target.value;
    setSelectedType(selectedTypeCode);
    setCurrentPage(1);

    if (selectedTypeCode) {
      try {
        const categories = await fetchEnergyDataCategories(selectedTypeCode);
        const filteredCategoriesMap = new Map();
        categories.forEach((category) => {
          const existing = filteredCategoriesMap.get(category.code);
          if (!existing) {
            filteredCategoriesMap.set(category.code, category);
          } else if (!existing.alternateName && category.alternateName) {
            filteredCategoriesMap.set(category.code, category);
          }
        });
        const uniqueCategories = Array.from(
          filteredCategoriesMap.values()
        ).sort((a, b) => a.description.localeCompare(b.description));
        setCategoryList(uniqueCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    } else {
      setCategoryList([]);
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const handleYearFromChange = (date) => {
    setSelectedYearFrom(date);
    setCurrentPage(1);
  };

  const handleYearToChange = (date) => {
    setSelectedYearTo(date);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleEntriesChange = (entries) => {
    setPageSize(entries);
    setCurrentPage(1);
  };

  const handleAddNewReport = async () => {
    try {
      if (!selectedCategory || !selectedType) {
        toast.error("Please select a category and type before adding a new report.");
        return;
      }
      const template = await fetchEnergyDataTemplateByCategoryAndType(selectedCategory, selectedType);
      if (!template) {
        toast.error("No form template found for the selected category and type.");
        return;
      }
      setSelectedTemplate(template);
      setSelectedData(null);
      setMode("create");
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("An error occurred while fetching the form template.");
    }
  };

  const handleEditReport = async (dataId) => {
    try {
      const existingData = await fetchEnergyDataById(dataId);
      const template = await fetchEnergyDataTemplateByCategoryAndType(
        existingData.category.code,
        existingData.type.code
      );
      if (!template) {
        toast.error("No form template found for the selected category and type.");
        return;
      }
      setSelectedTemplate(template);
      setSelectedData(existingData);
      setMode("edit");
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("An error occurred while fetching the form template.");
    }
  };

  const handleSaveReport = async (formData) => {
    setCurrentPage(1);
    try {
      await saveEnergyData(formData);
      const searchParams = {
        yearFrom: selectedYearFrom ? selectedYearFrom.getFullYear() : "",
        yearTo: selectedYearTo ? selectedYearTo.getFullYear() : "",
        category: selectedCategory,
        type: selectedType,
        page: 0,
        size: pageSize,
      };
      const result = await fetchEnergyData(searchParams);
      setData(result.content || []);
      setFilteredData(result.content || []);
      setTotalPages(result.totalPages || 0);
      setTotalElements(result.totalElements || 0);
      toast.success(mode === "create" ? "Energy data saved successfully!" : "Energy data updated successfully!");
      setShowModal(false);
    } catch (error) {
      console.error("Error saving energy data:", error);
      toast.error("An error occurred while saving energy data.");
    }
  };

  const handleDeleteReport = async (dataId) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this data?");
    if (isConfirmed) {
      try {
        await deleteEnergyData(dataId);
        const searchParams = {
          yearFrom: selectedYearFrom ? selectedYearFrom.getFullYear() : "",
          yearTo: selectedYearTo ? selectedYearTo.getFullYear() : "",
          category: selectedCategory,
          type: selectedType,
          page: currentPage - 1,
          size: pageSize,
        };
        const result = await fetchEnergyData(searchParams);
        setData(result.content || []);
        setFilteredData(result.content || []);
        setTotalPages(result.totalPages || 0);
        setTotalElements(result.totalElements || 0);
        toast.success("Energy data deleted successfully!");
      } catch (error) {
        console.error("Error deleting energy data:", error);
        toast.error("An error occurred while deleting energy data.");
      }
    }
  };

  const handleEditTemplate = () => {
    setTemplateJson(selectedTemplate || {});
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      await updateEnergyDataTemplate(templateJson.id, templateJson);
      toast.success("Template updated successfully!");
      if (selectedType) {
        const categories = await fetchEnergyDataCategories(selectedType);
        setCategoryList(categories);
      }
    } catch (error) {
      console.error("Error saving energy template:", error);
      toast.error("An error occurred while saving energy template.");
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedCategory || !selectedType) {
      toast.error("Please select a category and type before downloading the template.");
      return;
    }
    setIsDownloading(true);
    try {
      const searchParams = {
        category: selectedCategory,
        type: selectedType,
        templateId: selectedTemplate.id,
        yearFrom: selectedYearFrom ? selectedYearFrom.getFullYear() : "",
        yearTo: selectedYearTo ? selectedYearTo.getFullYear() : "",
      };
      await downloadEnergyTemplate(searchParams);
      toast.success("Template downloaded successfully.");
    } catch (error) {
      toast.error("An error occurred while downloading the template.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSynchronize = async () => {
    setIsSyncing(true);
    setSyncProgress(2); // Start with a small visible sliver
    
    // We want to reach ~95% over 60 seconds.
    // 60,000ms / 100 steps = 600ms per 1% increment.
    const interval = setInterval(() => {
      setSyncProgress((prev) => {
        if (prev >= 95) return prev; // Hold at 95% until API finishes
        return prev + 1;
      });
    }, 1800); 

    try {
      await synchronizeAllData();
      
      // Clear interval and snap to 100% immediately on success
      clearInterval(interval);
      setSyncProgress(100);
      toast.success("Server synchronization complete. Export file updated.");
    } catch (error) {
      clearInterval(interval);
      console.error("Error synchronizing data:", error);
      toast.error("An error occurred during synchronization.");
      setSyncProgress(0);
    } finally {
      // Keep the 100% bar visible for 1.5 seconds so user sees completion
      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
      }, 1500);
    }
  };

  return (
    <>
      <style>
        {`
          .sync-progress-container {
              width: 100%;
              background-color: #e9ecef;
              border-radius: 4px;
              margin: 15px 0;
              height: 12px;
              overflow: hidden;
              position: relative;
          }
          .sync-progress-bar {
              height: 100%;
              background-color: #0053a0;
              transition: width 0.4s ease;
          }
        `}
      </style>

      <div className="d-flex gap-2">
        <ClayButton.Group spaced={false}>
          <ClayButton displayType="primary" onClick={handleAddNewReport}>
            Add New Report
          </ClayButton>
          <ClayButton
            displayType="secondary"
            onClick={handleEditTemplate}
            disabled={!selectedTemplate}
          >
            Edit Report Template
          </ClayButton>
          <ClayButton
            displayType="secondary"
            onClick={handleSynchronize}
            disabled={isSyncing}
          >
            {isSyncing ? "Syncing..." : "Synchronize"}
          </ClayButton>
        </ClayButton.Group>
      </div>

      {/* Progress Bar Display */}
      {isSyncing && (
      <div style={{ marginBottom: "20px" }}>
        <div className="sync-progress-container">
          <div 
            className="sync-progress-bar" 
            style={{ width: `${syncProgress}%` }}
          ></div>
        </div>
        <small className="text-muted">
          {syncProgress < 100 
            ? `Syncing all data... this may take up to a minute (${syncProgress}%)` 
            : "Finalizing..."}
        </small>
      </div>
    )}

      <ClayLayout.Row style={{ marginTop: isSyncing ? "10px" : "20px" }}>
        <ClayLayout.Col size={4}>
          <ClaySelect id="type" value={selectedType} onChange={handleTypeChange}>
            <option value="">Select Energy</option>
            {typeList
              .slice()
              .sort((a, b) => a.description.localeCompare(b.description))
              .map((type, index) => (
                <option key={index} value={type.code}>
                  {type.description}
                </option>
              ))}
          </ClaySelect>
        </ClayLayout.Col>
        <ClayLayout.Col size={4}>
          <ClaySelect id="category" value={selectedCategory} onChange={handleCategoryChange}>
            <option value="">Select Process</option>
            {categoryList
              .slice()
              .sort((a, b) => {
                const nameA = a.alternateName || a.description;
                const nameB = b.alternateName || b.description;
                return nameA.localeCompare(nameB);
              })
              .map((category, index) => (
                <option key={index} value={category.code}>
                  {category.alternateName ? `${category.alternateName} - ` : ""}
                  {category.description}
                </option>
              ))}
          </ClaySelect>
        </ClayLayout.Col>
        <ClayLayout.Col size={2}>
          <DatePicker
            selected={selectedYearFrom}
            onChange={handleYearFromChange}
            showYearPicker
            dateFormat="yyyy"
            placeholderText="From Year"
            className="form-control"
          />
        </ClayLayout.Col>
        <ClayLayout.Col size={2}>
          <DatePicker
            selected={selectedYearTo}
            onChange={handleYearToChange}
            showYearPicker
            dateFormat="yyyy"
            placeholderText="To Year"
            className="form-control"
          />
        </ClayLayout.Col>
      </ClayLayout.Row>

      <Table style={{ marginTop: "20px" }}>
        <thead>
          <tr>
            <th>Energy</th>
            <th>Process</th>
            <th>Year</th>
            {filteredData.length > 0 &&
              Object.keys(filteredData[0].data || {}).map((key) => (
                <th key={key}>
                  {key
                    .replace(/[-_]/g, " ")
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </th>
              ))}
            <th>Date created</th>
            <th>Date modified</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item, index) => (
            <tr key={index}>
              <td>{item.type ? item.type.description : "N/A"}</td>
              <td>{item.category.description}</td>
              <td>{item.year}</td>
              {Object.keys(item.data || {}).map((key) => (
                <td key={key}>
                  {typeof item.data[key] === "object"
                    ? item.data[key].description || item.data[key].value || "N/A"
                    : item.data[key]}
                </td>
              ))}
              <td>{dayjs(item.createdDate).format("DD/MM/YYYY")}</td>
              <td>{dayjs(item.modifiedDate).format("DD/MM/YYYY")}</td>
              <td>
                <ClayButton.Group spaced={false}>
                  <ClayButton displayType="secondary" onClick={() => handleEditReport(item.id)}>
                    Edit
                  </ClayButton>
                  <ClayButton displayType="danger" onClick={() => handleDeleteReport(item.id)}>
                    Delete
                  </ClayButton>
                </ClayButton.Group>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex gap-2">
        <ClayButton
          displayType="secondary"
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            "Download Data"
          )}
        </ClayButton>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onEntriesChange={handleEntriesChange}
        entriesPerPage={pageSize}
      />

      {showModal && (
        <EnergyDataModal
          observer={observer}
          onClose={() => setShowModal(false)}
          onSave={handleSaveReport}
          selectedData={selectedData}
          selectedTemplate={selectedTemplate}
          selectedYearFrom={selectedYearFrom}
          selectedYearTo={selectedYearTo}
          mode={mode}
        />
      )}

      {showTemplateModal && (
        <TemplateDataModal
          show={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          templateJson={templateJson}
          setTemplateJson={setTemplateJson}
          onSave={handleSaveTemplate}
        />
      )}
    </>
  );
}

export default EnergyDataFormList;